import { useEffect, useState, useCallback, useRef } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, SEVERITY, PIECES } from "@/lib/constants";
import {
  Wrench, Play, Copy, CheckCircle2, XCircle, Navigation, StopCircle,
  Link2, ClipboardList, Clock, RefreshCw, Sparkles, LinkIcon,
  PlayCircle, PauseCircle, CalendarClock, Trash2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export const STOP_STATUS = {
  a_faire: { label: "À faire", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  en_cours: { label: "En cours", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  en_attente: { label: "En attente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  fait: { label: "Fait", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  indisponible: { label: "Indisponible", cls: "bg-red-50 text-red-700 border-red-200" },
  reporte: { label: "Reprogrammé", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  en_attente_approbation: { label: "À approuver", cls: "bg-violet-50 text-violet-700 border-violet-200" },
};

function haversineKm(a, b) {
  const r = 6371, dLat = (b.lat - a.lat) * Math.PI / 180, dLng = (b.lng - a.lng) * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}

function nearestOrder(stops, origin) {
  const remaining = [...stops];
  const order = [];
  let cur = origin;
  while (remaining.length) {
    let best = 0, bd = Infinity;
    remaining.forEach((s, i) => {
      const d = haversineKm(cur, { lat: s.latitude, lng: s.longitude });
      if (d < bd) { bd = d; best = i; }
    });
    cur = remaining.splice(best, 1)[0];
    order.push(cur);
  }
  return order;
}

function googleRoute(origin, stops, mode) {
  return new Promise((resolve) => {
    const ds = new window.google.maps.DirectionsService();
    ds.route({
      origin, destination: origin,
      waypoints: stops.map((s) => ({ location: { lat: s.latitude, lng: s.longitude }, stopover: true })),
      optimizeWaypoints: true, travelMode: mode,
    }, (res, status) => {
      if (status === "OK") resolve(res);
      else if (mode === "TWO_WHEELER") resolve(googleRoute(origin, stops, "DRIVING"));
      else resolve(null);
    });
  });
}

export function buildTourMessage(tour, link) {
  const lines = [
    `TOURNÉE MAINTENANCE — ${tour.date}`,
    `Suivi en direct : ${link}`,
    "",
  ];
  tour.stops.forEach((s, i) => {
    lines.push(`${i + 1}. ${s.name} — ${s.address}, ${s.postal_code} ${s.city}`);
    if (s.key_at_office) lines.push(`   Clé à récupérer au bureau${s.key_office_box ? ` (boîte n° ${s.key_office_box})` : ""}`);
    s.problems.forEach((p) => lines.push(`   - ${p.title} (${p.category}, ${p.severity}${p.room ? ", " + p.room : ""})`));
    lines.push("");
  });
  return lines.join("\n");
}

function StopMarker({ stop, index }) {
  const done = stop.status === "fait";
  const blocked = stop.status === "indisponible";
  const postponed = stop.status === "reporte";
  return (
    <AdvancedMarker position={{ lat: stop.lat, lng: stop.lng }} data-testid={`tour-marker-${stop.id}`}>
      <div className={`w-9 h-9 rounded-full flex items-center justify-center shadow-lg border-2 border-white ${
        done ? "bg-emerald-500" : blocked ? "bg-red-500" : postponed ? "bg-orange-500" : "bg-blue-600"}`}>
        {done ? <CheckCircle2 className="w-5 h-5 text-white" />
          : blocked ? <XCircle className="w-5 h-5 text-white" />
          : postponed ? <XCircle className="w-5 h-5 text-white" />
          : <span className="text-white text-sm font-bold">{index + 1}</span>}
      </div>
    </AdvancedMarker>
  );
}

function TourMapContent({ tour }) {
  const map = useMap();
  const dirRef = useRef(null);
  useEffect(() => {
    if (!map || !window.google) return;
    const remaining = tour.stops.filter((s) => !["fait", "indisponible", "reporte"].includes(s.status));
    const origin = tour.last_position
      ? { lat: tour.last_position.lat, lng: tour.last_position.lng }
      : tour.origin;
    if (!origin || remaining.length === 0) {
      if (dirRef.current) { dirRef.current.setMap(null); dirRef.current = null; }
      return;
    }
    if (!dirRef.current) {
      dirRef.current = new window.google.maps.DirectionsRenderer({
        map, suppressMarkers: true,
        polylineOptions: { strokeColor: "#2563EB", strokeWeight: 5, strokeOpacity: 0.85 },
      });
    }
    const pts = remaining.map((s) => ({ location: { lat: s.lat, lng: s.lng }, stopover: true }));
    const dest = pts.pop();
    new window.google.maps.DirectionsService().route(
      { origin, destination: dest.location, waypoints: pts, travelMode: "DRIVING" },
      (res, st) => { if (st === "OK" && dirRef.current) dirRef.current.setDirections(res); }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, tour.last_position?.at, tour.stops.map((s) => s.status).join(",")]);
  useEffect(() => {
    if (!map || !tour.stops.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    tour.stops.forEach((s) => bounds.extend({ lat: s.lat, lng: s.lng }));
    if (tour.last_position) bounds.extend({ lat: tour.last_position.lat, lng: tour.last_position.lng });
    map.fitBounds(bounds, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, tour.id]);
  return (
    <>
      {tour.stops.map((s, i) => <StopMarker key={s.id} stop={s} index={i} />)}
      {tour.last_position && (
        <AdvancedMarker position={{ lat: tour.last_position.lat, lng: tour.last_position.lng }}>
          <div className="relative" data-testid="tech-position-marker">
            <div className="w-5 h-5 rounded-full bg-sky-500 border-2 border-white shadow-lg" />
            <div className="absolute inset-0 rounded-full bg-sky-400 animate-ping opacity-40" />
          </div>
        </AdvancedMarker>
      )}
    </>
  );
}

function TourMap({ tour }) {
  const [mapType, setMapType] = useState("roadmap");
  if (!GMAPS_KEY) {
    return (
      <div className="h-64 rounded-2xl border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-sm text-slate-500 text-center px-6" data-testid="map-key-warning">
        Carte Google Maps non configurée — renseignez la clé API pour afficher le tracé en direct.
      </div>
    );
  }
  const center = tour.last_position || tour.origin || (tour.stops[0] && { lat: tour.stops[0].lat, lng: tour.stops[0].lng }) || { lat: 48.8566, lng: 2.3522 };
  return (
    <div className="relative h-72 md:h-96 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
      <APIProvider apiKey={GMAPS_KEY}>
        <Map defaultCenter={center} defaultZoom={13} mapId="DEMO_MAP_ID" mapTypeId={mapType}
             gestureHandling="greedy" disableDefaultUI zoomControl style={{ width: "100%", height: "100%" }}>
          <TourMapContent tour={tour} />
        </Map>
      </APIProvider>
      <button onClick={() => setMapType((m) => (m === "roadmap" ? "hybrid" : "roadmap"))}
              data-testid="map-type-toggle"
              className="absolute top-3 right-14 z-10 bg-white rounded-lg shadow-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
        {mapType === "roadmap" ? "Satellite" : "Plan"}
      </button>
    </div>
  );
}

export default function Maintenance() {
  const [tour, setTour] = useState(undefined);
  const [buildList, setBuildList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [buildRefresh, setBuildRefresh] = useState(0);
  const [excluded, setExcluded] = useState([]);
  const selectedList = buildList.filter((a) => !excluded.includes(a.id));
  const [bulkText, setBulkText] = useState("");
  const [bulkEntries, setBulkEntries] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [reportFor, setReportFor] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [resolveFor, setResolveFor] = useState(null);

  const load = useCallback(() => {
    api.get("/tours/active").then((r) => setTour(r.data)).catch(() => setTour(null));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    Promise.all([api.get("/apartments"), api.get("/problems")]).then(([a, p]) => {
      const list = a.data
        .map((apt) => ({ ...apt, openProblems: p.data.filter((x) => x.apartment_id === apt.id) }))
        .filter((x) => x.openProblems.length > 0 && x.latitude && x.longitude);
      setBuildList(list);
    }).catch(() => {});
  }, [tour?.id, buildRefresh]);

  const parseBulk = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/maintenance/parse-bulk", { text: bulkText });
      setBulkEntries(data.entries.map((e) => ({ ...e, checked: !!e.apartment_id && !e.paired_problem_id, priority: true })));
      if (!data.entries.length) toast.warning("Aucune anomalie détectée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analyse impossible");
    } finally { setBulkBusy(false); }
  };

  const setEntry = (i, patch) =>
    setBulkEntries((arr) => arr.map((e, j) => (j === i ? { ...e, ...patch } : e)));

  const confirmBulk = async () => {
    const toCreate = bulkEntries.filter((e) => e.checked && e.apartment_id && !e.paired_problem_id);
    const pairedSelected = bulkEntries.filter((e) => e.checked && e.apartment_id && e.paired_problem_id);
    if (!toCreate.length && !pairedSelected.length) { toast.warning("Cochez au moins une ligne"); return; }
    setBulkBusy(true);
    try {
      let created = 0;
      if (toCreate.length) {
        const { data } = await api.post("/maintenance/confirm-bulk", {
          entries: toCreate.map((e) => ({
            apartment_id: e.apartment_id, title: e.title, description: e.description,
            category: e.category, severity: e.severity, room: e.room,
          })),
        });
        created = data.created;
      }
      const paired = bulkEntries.filter((e) => e.paired_problem_id).length;
      const priorityIds = new Set([
        ...toCreate.filter((e) => e.priority !== false).map((e) => e.apartment_id),
        ...pairedSelected.map((e) => e.apartment_id),
      ]);
      const msgs = [];
      if (created) msgs.push(`${created} anomalie(s) créée(s)`);
      if (paired) msgs.push(`${paired} appariée(s) à l'existant`);
      toast.success(`${msgs.join(" — ")} — planning du jour mis à jour (modifiable ci-dessous)`);
      setBulkText(""); setBulkEntries(null);
      const [a, p] = await Promise.all([api.get("/apartments"), api.get("/problems")]);
      const list = a.data
        .map((apt) => ({ ...apt, openProblems: p.data.filter((x) => x.apartment_id === apt.id) }))
        .filter((x) => x.openProblems.length > 0 && x.latitude && x.longitude);
      setBuildList(list);
      setExcluded(list.filter((x) => !priorityIds.has(x.id)).map((x) => x.id));
      setBuildRefresh((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création");
    } finally { setBulkBusy(false); }
  };

  const generate = async () => {
    if (!selectedList.length) { toast.warning("Sélectionnez au moins un appartement"); return; }
    setBusy(true);
    try {
      const origin = await new Promise((res) =>
        navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => res(null), { timeout: 8000 }));
      if (!origin) toast.warning("Position GPS indisponible — itinéraire calculé sans point de départ");
      let ordered = null;
      if (GMAPS_KEY && window.google) {
        const res = await googleRoute(origin || { lat: selectedList[0].latitude, lng: selectedList[0].longitude }, selectedList, "TWO_WHEELER");
        if (res) ordered = res.routes[0].waypoint_order.map((i) => selectedList[i]);
      }
      if (!ordered) ordered = nearestOrder(selectedList, origin || { lat: selectedList[0].latitude, lng: selectedList[0].longitude });
      const { data } = await api.post("/tours", { stops: ordered.map((s) => s.id), origin });
      setTour(data);
      toast.success("Planning du jour généré — itinéraire optimisé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la génération");
    } finally { setBusy(false); }
  };

  const closeTour = async () => {
    try {
      await api.post(`/tours/${tour.id}/close`);
      toast.success("Tournée clôturée");
      setTour(null);
    } catch { toast.error("Erreur"); }
  };

  const regenerateLink = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/tours/${tour.id}/regenerate-link`);
      setTour((t) => ({ ...t, token: r.data.token, token_expires_at: r.data.token_expires_at }));
      toast.success("Nouveau lien généré — l'ancien lien est révoqué");
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const approveStop = async (stopId) => {
    try {
      await api.post(`/tours/${tour.id}/stops/${stopId}/approve`);
      toast.success("Étape approuvée — trajet ré-optimisé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const rejectStop = async (stopId) => {
    try {
      await api.post(`/tours/${tour.id}/stops/${stopId}/reject`);
      toast.success("Étape retirée du planning");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const removeStop = async (stopId) => {
    if (!window.confirm("Retirer cette mission du planning ? L'anomalie restera ouverte.")) return;
    try {
      await api.post(`/tours/${tour.id}/stops/${stopId}/remove`);
      toast.success("Mission retirée du planning");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const stopAction = async (stopId, action, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/tours/${tour.id}/stops/${stopId}/status`, { action, ...extra });
      toast.success(action === "reporte" ? "Intervention reprogrammée" : "Statut mis à jour");
      setReportFor(null); setReportReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const stopResolve = async (stopId, resolved) => {
    setBusy(true);
    try {
      await api.post(`/tours/${tour.id}/stops/${stopId}/resolve`, { resolved });
      toast.success(resolved ? "Étape terminée — anomalie résolue" : "L'anomalie reste ouverte");
      setResolveFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (tour === undefined) return <div className="p-8 text-slate-500">Chargement…</div>;

  const trackLink = tour ? `${window.location.origin}/maintenance/suivi/${tour.token}` : "";
  const pct = tour?.progress ? Math.round((tour.progress.done / Math.max(1, tour.progress.total)) * 100) : 0;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6" data-testid="maintenance-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-display text-slate-900 flex items-center gap-2">
            <Wrench className="w-6 h-6 text-blue-600" /> Maintenance
          </h1>
          <p className="text-sm text-slate-500 mt-1">Tournée des appartements avec anomalies à traiter</p>
        </div>
      </div>

      {!tour ? (
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="bulk-card">
            <h2 className="font-bold font-display text-slate-900 mb-1 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-600" /> Ajout d'anomalies en vrac
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              Collez vos notes ou un tableau brut — l'IA identifie les appartements, détecte les doublons déjà enregistrés et prépare la planification du jour.
            </p>
            <Textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={4}
                      placeholder="Ex : Meslay Studio CLIENT 09/09/2026… Réparer la douche et la porte de la douche"
                      data-testid="bulk-text" className="text-sm" />
            <div className="flex justify-end mt-3">
              <Button onClick={parseBulk} disabled={bulkBusy || bulkText.trim().length < 5}
                      data-testid="bulk-parse-btn" variant="outline">
                {bulkBusy && !bulkEntries ? <Clock className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                Analyser avec l'IA
              </Button>
            </div>

            {bulkEntries && (
              <div className="mt-4 space-y-2" data-testid="bulk-preview">
                <p className="text-[11px] text-slate-400">
                  La mise au planning du jour est une proposition : ajustez-la ligne par ligne, puis librement dans la liste du planning.
                </p>
                {bulkEntries.map((e, i) => (
                  <div key={i} className={`border rounded-xl p-3 ${e.apartment_id ? "border-slate-200" : "border-red-200 bg-red-50/40"}`}
                       data-testid={`bulk-row-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Checkbox checked={e.checked} disabled={!e.apartment_id}
                                onCheckedChange={(v) => setEntry(i, { checked: !!v })}
                                data-testid={`bulk-check-${i}`} />
                      <span className="text-sm font-semibold text-slate-800">{e.apartment_name || e.apartment_name_raw || "Appartement ?"}</span>
                      {!e.apartment_id && (
                        <Badge className="bg-red-50 text-red-700 border-red-200 border" data-testid={`bulk-status-${i}`}>
                          Appartement introuvable
                        </Badge>
                      )}
                      {e.paired_problem_id && (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 border" data-testid={`bulk-status-${i}`}>
                          <LinkIcon className="w-3 h-3 mr-1" /> Appairé : {e.paired_problem_title}
                        </Badge>
                      )}
                      {e.apartment_id && !e.paired_problem_id && (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid={`bulk-status-${i}`}>
                          Nouveau
                        </Badge>
                      )}
                      {e.apartment_id && !e.paired_problem_id && (
                        <button type="button" onClick={() => setEntry(i, { priority: !e.priority })}
                                data-testid={`bulk-priority-${i}`}
                                title="Proposition : inclure au planning du jour — cliquez pour changer"
                                className={`ml-auto inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full border transition-colors ${
                                  e.priority
                                    ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                                    : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"}`}>
                          <CalendarClock className="w-3 h-3" />
                          {e.priority ? "Planning du jour" : "Plus tard"}
                        </button>
                      )}
                    </div>
                    <Input value={e.title} onChange={(ev) => setEntry(i, { title: ev.target.value })}
                           className="mt-2 h-8 text-sm" data-testid={`bulk-title-${i}`} />
                    <Textarea value={e.description} onChange={(ev) => setEntry(i, { description: ev.target.value })}
                              rows={2} className="mt-1.5 text-xs" data-testid={`bulk-desc-${i}`} />
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <Select value={e.category} onValueChange={(v) => setEntry(i, { category: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`bulk-category-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={e.severity} onValueChange={(v) => setEntry(i, { severity: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`bulk-severity-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(SEVERITY).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={e.room || "none"} onValueChange={(v) => setEntry(i, { room: v === "none" ? null : v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`bulk-room-${i}`}><SelectValue placeholder="Pièce" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Pièce —</SelectItem>
                          {PIECES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="ghost" size="sm" onClick={() => setBulkEntries(null)} data-testid="bulk-cancel-btn">
                    Annuler
                  </Button>
                  <Button size="sm" onClick={confirmBulk} disabled={bulkBusy}
                          data-testid="bulk-confirm-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                    {bulkBusy ? <Clock className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                    {bulkEntries.some((e) => e.checked && e.apartment_id && !e.paired_problem_id)
                      ? "Créer les anomalies sélectionnées"
                      : "Sélectionner pour le planning"}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold font-display text-slate-900">
                Appartements à traiter ({selectedList.length}/{buildList.length} sélectionnés)
              </h2>
              <Button onClick={generate} disabled={busy || selectedList.length === 0} data-testid="generate-tour-btn"
                      className="bg-blue-600 hover:bg-blue-700 text-white">
                {busy ? <Clock className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
                Générer le planning du jour ({selectedList.length})
              </Button>
            </div>
            {buildList.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-6">Aucune anomalie ouverte — rien à planifier.</p>
            )}
            <div className="space-y-2">
              {buildList.map((a) => (
                <div key={a.id} className={`flex items-center justify-between border rounded-xl px-4 py-3 transition-colors ${
                       excluded.includes(a.id) ? "border-slate-100 bg-slate-50/50 opacity-50" : "border-slate-100"}`}
                     data-testid={`build-item-${a.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Checkbox checked={!excluded.includes(a.id)}
                              onCheckedChange={(v) => setExcluded((ex) => v ? ex.filter((x) => x !== a.id) : [...ex, a.id])}
                              data-testid={`build-check-${a.id}`} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{a.name}</div>
                      <div className="text-xs text-slate-500 truncate">{a.address}, {a.postal_code} {a.city}</div>
                    </div>
                  </div>
                  <Badge className="bg-red-50 text-red-700 border-red-200 border flex-shrink-0">
                    {a.openProblems.length} anomalie{a.openProblems.length > 1 ? "s" : ""}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid="tour-live-panel">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-bold font-display text-slate-900">Tournée du {tour.date}</h2>
              <span className="text-xs text-slate-500">
                {tour.last_position
                  ? `Technicien localisé ${new Date(tour.last_position.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`
                  : "En attente de la position du technicien"}
              </span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden" data-testid="tour-progress">
                <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-sm font-bold text-slate-800" data-testid="tour-progress-label">
                {tour.progress?.done ?? 0}/{tour.progress?.total ?? tour.stops.length} ({pct}%)
              </span>
            </div>
            <TourMap tour={tour} />
            <div className="flex flex-wrap gap-2 mt-4">
              <Button variant="outline" size="sm" data-testid="copy-track-link"
                      onClick={() => { navigator.clipboard.writeText(trackLink); toast.success("Lien de suivi copié"); }}>
                <Link2 className="w-4 h-4 mr-1.5" /> Lien de suivi technicien
              </Button>
              <Button variant="outline" size="sm" data-testid="copy-tour-message"
                      onClick={() => { navigator.clipboard.writeText(buildTourMessage(tour, trackLink)); toast.success("Message copié"); }}>
                <Copy className="w-4 h-4 mr-1.5" /> Message à copier-coller
              </Button>
              <Button variant="outline" size="sm" data-testid="revoke-track-link" onClick={regenerateLink}
                      disabled={busy}
                      title="Révoque le lien actuel et en génère un nouveau (valide 48 h)">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Révoquer le lien
              </Button>
              <Button variant="ghost" size="sm" onClick={closeTour} data-testid="close-tour-btn"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50">
                <StopCircle className="w-4 h-4 mr-1.5" /> Clôturer la tournée
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h2 className="font-bold font-display text-slate-900 mb-3 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-slate-500" /> Étapes
            </h2>
            <div className="space-y-2">
              {tour.stops.map((s, i) => (
                <div key={s.id} className="border border-slate-100 rounded-xl px-4 py-3" data-testid={`tour-stop-${s.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      {s.status === "fait" ? <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        : s.status === "indisponible" ? <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        : <Navigation className="w-5 h-5 text-blue-500 flex-shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-800 truncate">{i + 1}. {s.name}</div>
                        <div className="text-xs text-slate-500 truncate">{s.address}, {s.postal_code} {s.city}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Badge className={`${STOP_STATUS[s.status]?.cls} border`} data-testid={`stop-status-${s.id}`}>
                        {STOP_STATUS[s.status]?.label}
                      </Badge>
                      {tour.active && ["a_faire", "en_attente", "reporte"].includes(s.status) && (
                        <button onClick={() => removeStop(s.id)} data-testid={`remove-stop-${s.id}`}
                                title="Retirer cette mission du planning (l'anomalie reste ouverte)"
                                className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-1.5 ml-7 space-y-0.5">
                    {s.key_at_office && <div className="text-xs text-sky-700 font-medium">Clé à récupérer au bureau{s.key_office_box ? ` (boîte n° ${s.key_office_box})` : ""}</div>}
                    {s.problems.map((p) => (
                      <div key={p.id} className="text-xs text-slate-600">• {p.title} ({p.severity})</div>
                    ))}
                    {s.reschedules?.map((r, j) => (
                      <div key={j} className="text-xs text-orange-600">
                        Reprogrammé : {r.reason} — {new Date(r.at).toLocaleString("fr-FR")}
                      </div>
                    ))}
                    {s.status === "en_attente_approbation" && (
                      <div className="flex gap-2 mt-2" data-testid={`pending-approval-${s.id}`}>
                        <Button size="sm" onClick={() => approveStop(s.id)} data-testid={`approve-stop-${s.id}`}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs">
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approuver — optimiser le trajet
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => rejectStop(s.id)} data-testid={`reject-stop-${s.id}`}
                                className="text-red-500 border-red-200 hover:bg-red-50 h-7 text-xs">
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                        </Button>
                      </div>
                    )}
                    {tour.active && !["fait", "indisponible", "en_attente_approbation"].includes(s.status) && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`mgr-actions-${s.id}`}>
                        {["a_faire", "en_attente"].includes(s.status) && (
                          <Button size="sm" variant="outline" disabled={busy} data-testid={`mgr-start-${s.id}`}
                                  onClick={() => stopAction(s.id, "en_cours")}
                                  className="h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-50">
                            <PlayCircle className="w-3.5 h-3.5 mr-1" /> Mission en cours
                          </Button>
                        )}
                        {s.status === "en_cours" && (
                          <>
                            <Button size="sm" disabled={busy} data-testid={`mgr-finish-${s.id}`}
                                    onClick={() => setResolveFor(s.id)}
                                    className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Intervention terminée
                            </Button>
                            <Button size="sm" variant="outline" disabled={busy} data-testid={`mgr-pause-${s.id}`}
                                    onClick={() => stopAction(s.id, "en_attente")} className="h-7 text-xs">
                              <PauseCircle className="w-3.5 h-3.5 mr-1" /> En attente
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" disabled={busy} data-testid={`mgr-report-${s.id}`}
                                onClick={() => { setReportFor(s.id); setReportReason(""); }}
                                className="h-7 text-xs text-orange-600 border-orange-200 hover:bg-orange-50">
                          <CalendarClock className="w-3.5 h-3.5 mr-1" /> Reprogrammer
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h2 className="font-bold font-display text-slate-900 mb-2">Message à envoyer au technicien</h2>
            <Textarea readOnly value={buildTourMessage(tour, trackLink)} rows={8} data-testid="tour-message"
                      className="text-xs font-mono bg-slate-50" />
          </div>
        </div>
      )}

      <Dialog open={!!reportFor} onOpenChange={(o) => !o && setReportFor(null)}>
        <DialogContent data-testid="mgr-report-dialog">
          <DialogHeader><DialogTitle>Reprogrammer l'intervention</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Pourquoi l'intervention ne peut-elle pas avoir lieu maintenant ?</p>
          <Select value={reportReason} onValueChange={setReportReason}>
            <SelectTrigger data-testid="mgr-report-reason"><SelectValue placeholder="Choisir un motif…" /></SelectTrigger>
            <SelectContent>
              {["accès impossible", "clé manquante", "client présent", "autre"].map((r) => (
                <SelectItem key={r} value={r} data-testid={`mgr-reason-${r}`}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReportFor(null)}>Annuler</Button>
            <Button onClick={() => stopAction(reportFor, "reporte", { reason: reportReason })}
                    disabled={!reportReason || busy} data-testid="mgr-report-confirm"
                    className="bg-orange-600 hover:bg-orange-700 text-white">Reprogrammer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resolveFor} onOpenChange={(o) => !o && setResolveFor(null)}>
        <DialogContent data-testid="mgr-resolve-dialog">
          <DialogHeader><DialogTitle>Le problème a-t-il été complètement résolu ?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Si oui, l'anomalie sera marquée « Résolu » et disparaîtra de l'écran Anomalies.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => stopResolve(resolveFor, false)} disabled={busy}
                    data-testid="mgr-resolve-no">Non, pas complètement</Button>
            <Button onClick={() => stopResolve(resolveFor, true)} disabled={busy} data-testid="mgr-resolve-yes"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">Oui, résolu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
